import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, MessageSquare, CreditCard } from 'lucide-react';

interface SendInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (method: 'email' | 'sms', allowStripePay: boolean) => void;
  customerEmail?: string;
  customerPhone?: string;
  hasStripeConnected?: boolean;
  isLoading?: boolean;
  loadingMethod?: 'email' | 'sms' | null;
}

export default function SendInvoiceModal({
  isOpen,
  onClose,
  onSelectMethod,
  customerEmail,
  customerPhone,
  hasStripeConnected = false,
  isLoading = false,
  loadingMethod = null
}: SendInvoiceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [emailAllowStripePay, setEmailAllowStripePay] = useState(true);
  const [smsAllowStripePay, setSmsAllowStripePay] = useState(true);

  React.useEffect(() => {
    console.log('SendInvoiceModal - isOpen changed:', isOpen);
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  const handleSelectMethod = (method: 'email' | 'sms') => {
    if (isLoading) return; // Don't allow selection while loading
    
    const allowStripePay = method === 'email' ? emailAllowStripePay : smsAllowStripePay;
    console.log('SendInvoiceModal - Method selected:', method, 'Allow Stripe Pay:', allowStripePay);
    onSelectMethod(method, allowStripePay);
    
    // Close the modal after selecting method
    handleClose();
  };

  console.log('SendInvoiceModal - Rendering, isOpen:', isOpen, 'isAnimating:', isAnimating);
  
  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[200000] transition-all duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto pointer-events-none">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div 
            className={`relative bg-white rounded-lg shadow-xl overflow-hidden transform transition-all duration-300 ease-out ${
              isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            } w-full max-w-md mx-4 pointer-events-auto`}
          >
            {/* Header - Clean Modern Design */}
            <div className="bg-gray-900 border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Send Invoice</h3>
                    <p className="text-sm text-gray-300">
                      Choose how to send the invoice to your customer
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Email Option */}
              <button
                onClick={() => handleSelectMethod('email')}
                disabled={!customerEmail || (isLoading && loadingMethod === 'email')}
                className={`w-full p-4 rounded-lg border transition-colors ${
                  customerEmail && !(isLoading && loadingMethod === 'email')
                    ? 'border-gray-200 bg-white hover:bg-gray-50 hover:border-blue-300 cursor-pointer'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-lg ${
                    customerEmail && !(isLoading && loadingMethod === 'email') ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isLoading && loadingMethod === 'email' ? (
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Mail className="w-6 h-6" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <p className="text-sm text-gray-600">
                      {isLoading && loadingMethod === 'email' 
                        ? 'Sending invoice...' 
                        : customerEmail || 'No email address available'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Stripe Pay Checkbox */}
                {hasStripeConnected && customerEmail && (
                  <div className="mt-3 flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      id="email-stripe-pay"
                      checked={emailAllowStripePay}
                      onChange={(e) => setEmailAllowStripePay(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="email-stripe-pay" className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                      <img src="/Stripe (2).png" alt="Stripe" className="w-6 h-6" />
                      <span>Stripe Payment Link</span>
                    </label>
                  </div>
                )}
              </button>

              {/* SMS Option - Coming Soon */}
              <div className="w-full p-4 rounded-lg border border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed">
                <div className="flex items-center space-x-4">
                  <div className="p-3 rounded-lg bg-gray-100 text-gray-400">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-500">SMS</h4>
                    <p className="text-sm text-gray-500">Coming soon</p>
                  </div>
                  <div className="px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                    Soon
                  </div>
                </div>
                
                {/* Stripe Pay Checkbox - Disabled for SMS */}
                {hasStripeConnected && (
                  <div className="mt-3 flex items-center space-x-2 opacity-50">
                    <input
                      type="checkbox"
                      id="sms-stripe-pay"
                      checked={smsAllowStripePay}
                      disabled
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="sms-stripe-pay" className="flex items-center space-x-2 text-sm text-gray-500 cursor-not-allowed">
                      <img src="/Stripe (2).png" alt="Stripe" className="w-6 h-6 opacity-50" />
                      <span>Stripe Payment Link</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleClose}
                className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}